import apiClient from "./apiClient";
import type { UnifiedSubscriptionView } from '../types/unifiedSubscription';
import type { ClientSource } from "./tenantDeletionApi";

// Module structure / helpers — single source of truth lives in `lib/modules`.
// Re-exported here for backward compatibility with existing imports
// (`TenantDetails`, `useTenants`, `useImpersonation`, `services/api` barrel, etc.).
// New consumers should import directly from `../lib/modules`.
export {
  MODULE_STRUCTURE,
  MODULE_DISPLAY_NAMES,
  AVAILABLE_MODULES,
  getModuleDisplayName,
  getModulesByCategory,
  isWildcardModule,
  getWildcardBase,
  getSubmodulesForWildcard,
  hasModuleAccess,
  type ModuleName,
} from "../lib/modules";

export interface TenantSubscription {
  id: string;
  status:
    | "incomplete"
    | "trialing"
    | "active"
    | "past_due"
    | "unpaid"
    | "canceled"
    | "suspended";
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  /** ISO 8601 timestamp when this subscription was migrated to the new Billing BC. Null if not yet migrated. */
  migratedAt: string | null;
  plan: {
    id: string;
    name: string;
  };
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  nip?: string;
  customDomains: string[];
  enabledModules: string[];
  title?: string;
  description?: string;
  brandId?: string;
  createdAt: string;
  updatedAt: string;
  userTenants?: UserTenant[];
  brand?: {
    id: string;
    displayName: string;
    primaryColor: string;
  };
  /**
   * @deprecated AC-S8: legacy field used only by `/admin/tenants` list view
   * (`findAll()` still returns it). `findOne()` does NOT load this relation.
   * Tab "Subskrypcje" w `/admin/tenants/:id` reads billing subscriptions
   * directly via `useBillingSubscriptionsQuery` with the `tenantId` filter.
   */
  subscriptions?: TenantSubscription[];
  activeSubscription?: UnifiedSubscriptionView | null;
  /** Inbound/outbound attribution set by admin. Null for tenants without an assigned source. */
  clientSource?: ClientSource | null;
}

export interface UserTenant {
  id: string;
  userId: string;
  tenantId: string;
  role: "ADMIN" | "SALES" | "USER";
  createdAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface CreateTenantPayload {
  name: string;
  subdomain: string;
  nip: string;
  customDomains?: string[];
  enabledModules?: string[];
  brandId?: string;
  // User creation fields
  userEmail?: string;
  userPassword?: string;
  userFirstName?: string;
  userLastName?: string;
  userRole?: "ADMIN" | "SALES" | "USER";
}

export interface UpdateTenantPayload {
  name?: string;
  subdomain?: string;
  nip?: string;
  customDomains?: string[];
  enabledModules?: string[];
  title?: string;
  description?: string;
  brandId?: string;
  /** Inbound/outbound attribution. Omit to leave unchanged. */
  clientSource?: ClientSource;
  /** Manual "NIE WYSYŁAĆ" marketing block (AC-1/AC-2). Omit to leave unchanged. */
  marketingOptOut?: boolean;
  /** Free-text note for marketingOptOut. `null` clears it. Omit to leave unchanged. */
  marketingOptOutNote?: string | null;
}

export interface DomainOperationResult {
  domain: string;
  success: boolean;
  error?: string;
}

export interface UpdateTenantResult {
  tenant: Tenant;
  domainOperations?: DomainOperationResult[];
}

export interface AddUserToTenantPayload {
  email: string;
  role: "ADMIN" | "SALES" | "USER";
}

// Tenant CRUD operations
export const getTenants = async (): Promise<Tenant[]> => {
  const { data } = await apiClient.get("/tenants");
  return data;
};

export const getTenant = async (tenantId: string): Promise<Tenant> => {
  const { data } = await apiClient.get(`/tenants/${tenantId}`);
  return data;
};

export const createTenant = async (
  tenantData: CreateTenantPayload
): Promise<Tenant> => {
  const { data } = await apiClient.post("/tenants", tenantData);
  // Backend returns { tenant: Tenant, user: User | null } for user creation
  // Extract just the tenant part for compatibility
  return data.tenant || data;
};

export const updateTenant = async (
  tenantId: string,
  tenantData: UpdateTenantPayload
): Promise<UpdateTenantResult> => {
  const { data } = await apiClient.patch(`/tenants/${tenantId}`, tenantData);
  return data;
};

export const deleteTenant = async (tenantId: string): Promise<void> => {
  await apiClient.delete(`/tenants/${tenantId}`);
};

// Module management
export const updateTenantModules = async (
  tenantId: string,
  enabledModules: string[]
): Promise<Tenant> => {
  const { data } = await apiClient.patch(`/tenants/${tenantId}/modules`, {
    modules: enabledModules,
  });
  return data;
};

// User assignment
export const addUserToTenant = async (
  tenantId: string,
  userData: AddUserToTenantPayload
): Promise<UserTenant> => {
  const { data } = await apiClient.post(`/tenants/${tenantId}/users`, userData);
  return data;
};

export const removeUserFromTenant = async (
  tenantId: string,
  userId: string
): Promise<void> => {
  await apiClient.delete(`/tenants/${tenantId}/users/${userId}`);
};

// =====================================================
// Tenant Details APIs (for admin panel)
// =====================================================

export interface TenantMember {
  id: string;
  userId: string;
  tenantId: string;
  tenantRole: "tenant_admin" | "tenant_user";
  globalRole: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  /** Computed: true if user has password set (passwordHash !== null) */
  hasPassword: boolean;
  /** Per-user privilege modules (admin:* namespace only). Populated by backend when present. */
  enabledModules?: string[];
  joinedAt: string;
  updatedAt: string;
  createdAt: string;
}

export interface TenantHotel {
  id: string;
  name: string;
  subdomain: string;
  customDomains: string[] | null;
  isActive: boolean;
  createdAt: string;
  offersCount: number;
}

/**
 * Get users for a specific tenant
 */
export const getTenantUsers = async (
  tenantId: string
): Promise<TenantMember[]> => {
  const { data } = await apiClient.get(`/tenants/${tenantId}/users`);
  return data;
};

/**
 * Seat usage for a tenant (spec 2026-06-16-tenant-seat-limit-enforcement).
 * `maxSeats`/`remaining` are null when the plan has no seat limit.
 */
export interface SeatUsage {
  maxSeats: number | null;
  used: number;
  remaining: number | null;
}

/**
 * Get seat usage (plan limit, used, remaining) for a tenant.
 * @param tenantId - Tenant ID
 */
export const getSeatUsage = async (
  tenantId: string
): Promise<SeatUsage> => {
  const { data } = await apiClient.get(`/tenants/${tenantId}/seats`);
  return data;
};

/**
 * Resend invite link to user without password
 * @param tenantId - Tenant ID
 * @param userId - User ID to resend invite to
 * @returns Success response with message
 */
export const resendInvite = async (
  tenantId: string,
  userId: string
): Promise<{ success: boolean; message: string }> => {
  const { data } = await apiClient.post(
    `/tenants/${tenantId}/users/${userId}/resend-invite`
  );
  return data;
};

/**
 * Get hotels for a specific tenant with offer counts
 */
export const getTenantHotels = async (
  tenantId: string
): Promise<TenantHotel[]> => {
  const { data } = await apiClient.get(`/tenants/${tenantId}/hotels`);
  return data;
};

// =====================================================
// Impersonation APIs
// =====================================================

export interface ImpersonationResponse {
  access_token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    impersonatorId: string | null;
    userTenants?: Array<{
      tenantId: string;
      role: string;
      enabledModules: string[];
      enabledModulesApi: string[];
      tenant?: {
        id: string;
        name: string;
        subdomain: string;
      };
    }>;
  };
}

/**
 * Start impersonating a user (admin only)
 */
export const impersonateUser = async (
  userId: string
): Promise<ImpersonationResponse> => {
  const { data } = await apiClient.post(`/auth/impersonate/${userId}`);
  return data;
};

/**
 * Stop impersonating and return to admin account
 */
export const stopImpersonation = async (): Promise<ImpersonationResponse> => {
  const { data } = await apiClient.post("/auth/stop-impersonation");
  return data;
};

// =====================================================
// Per-user module grants (privilege management)
// =====================================================

/**
 * Update per-user enabled modules (privilege grants).
 * Only admin:* namespace modules (privilege) are accepted by the backend.
 * Global admin only — endpoint returns 403 for non-admins.
 *
 * @param tenantId - Tenant ID
 * @param userId - User ID to update modules for
 * @param enabledModules - Full list of enabled modules to set (replaces existing)
 */
export const updateUserModules = async (
  tenantId: string,
  userId: string,
  enabledModules: string[]
): Promise<void> => {
  await apiClient.patch(`/tenants/${tenantId}/users/${userId}/modules`, {
    enabledModules,
  });
};
